import { useState, useEffect } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const TeamPerformanceChart = () => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Fetch real team performance data
  const { data: teamData = [], isLoading } = useQuery({
    queryKey: ['team-performance'],
    queryFn: async () => {
      // Get all users with their upload and transaction stats
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('id, first_name, last_name');

      if (usersError) throw usersError;

      if (!users || users.length === 0) return [];

      // For each user, get their stats
      const performanceData = await Promise.all(users.map(async (user) => {
        // Count uploads
        const { count: uploadCount } = await supabase
          .from('file_uploads')
          .select('*', { count: 'exact', head: true })
          .eq('uploaded_by', user.id);

        // Calculate accuracy from validated transactions
        const { data: transactions } = await supabase
          .from('transactions')
          .select('ai_classification_confidence')
          .not('ai_classification_confidence', 'is', null);

        const avgAccuracy = transactions && transactions.length > 0
          ? transactions.reduce((sum, t) => sum + (t.ai_classification_confidence || 0), 0) / transactions.length
          : 0;

        return {
          name: `${user.first_name} ${user.last_name}`,
          filesProcessed: uploadCount || 0,
          workloadCapacity: Math.max(uploadCount || 0, 100), // Set capacity as 100 or current files
          accuracy: avgAccuracy,
        };
      }));

      return performanceData.filter(p => p.filesProcessed > 0); // Only show active users
    }
  });

  if (!isMounted || isLoading) {
    return (
      <div className="bg-white p-6 rounded-2xl border border-brand-border xl:col-span-2">
        <div className="h-[280px] flex items-center justify-center">
          <div className="text-brand-text-secondary">Loading chart...</div>
        </div>
      </div>
    );
  }

  if (teamData.length === 0) {
    return (
      <div className="bg-white p-6 rounded-2xl border border-brand-border xl:col-span-2">
        <div className="h-[280px] flex items-center justify-center">
          <div className="text-brand-text-secondary">No team performance data available</div>
        </div>
      </div>
    );
  }

  try {
    return (
      <div className="bg-white p-6 rounded-2xl border border-brand-border xl:col-span-2">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-brand-primary">Team Performance & Workload</h3>
          <button className="h-7 px-2 py-1.5 bg-brand-secondary rounded-[7px] justify-start items-center gap-0.5 inline-flex text-center text-brand-primary text-[10px] leading-[10px]">
            Last 30 Days <i className="fa-solid fa-caret-down ml-1"></i>
          </button>
        </div>
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={teamData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--brand-border))" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12, fill: 'hsl(var(--brand-text-secondary))' }}
                axisLine={{ stroke: 'hsl(var(--brand-border))' }}
              />
              <YAxis 
                yAxisId="files"
                tick={{ fontSize: 12, fill: 'hsl(var(--brand-text-secondary))' }}
                axisLine={{ stroke: 'hsl(var(--brand-border))' }}
              />
              <YAxis 
                yAxisId="accuracy" 
                orientation="right"
                domain={[95, 100]}
                tick={{ fontSize: 12, fill: 'hsl(var(--brand-text-secondary))' }}
                axisLine={{ stroke: 'hsl(var(--brand-border))' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'hsl(var(--brand-white))',
                  border: '1px solid hsl(var(--brand-border))',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '12px', color: 'hsl(var(--brand-text-secondary))' }}
              />
              <Bar 
                yAxisId="files"
                dataKey="filesProcessed" 
                fill="hsl(var(--brand-primary))" 
                name="Files Processed"
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                yAxisId="files"
                dataKey="workloadCapacity" 
                fill="hsl(var(--brand-secondary))" 
                name="Workload Capacity"
                radius={[4, 4, 0, 0]}
              />
              <Line 
                yAxisId="accuracy"
                type="monotone" 
                dataKey="accuracy" 
                stroke="hsl(var(--brand-accent))" 
                strokeWidth={2}
                name="Accuracy (%)"
                dot={{ fill: 'hsl(var(--brand-accent))', strokeWidth: 2, r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  } catch (error) {
    return (
      <div className="bg-white p-6 rounded-2xl border border-brand-border xl:col-span-2">
        <div className="h-[280px] flex items-center justify-center">
          <div className="text-brand-text-secondary">Chart unavailable</div>
        </div>
      </div>
    );
  }
};